import { useState, useEffect } from 'react';
import { usePublicClient, useWalletClient } from 'wagmi';
import { useAppKitAccount } from '@reown/appkit/react';
import { environment } from '@/config/environment';
import { TOKEN_LOCK_ABI } from '@/contracts/TokenLock.abi';
import { TOKEN_LOCK_FACTORY_ABI } from '@/contracts/TokenLockFactory.abi';
import { ERC20_ABI } from '@/contracts/ERC20.abi';
import { Category } from '@/hooks/useWeb3';
import { createRpcReader, isRetryableRpcError } from '@/utils/rpcRead';
import { decodeEventLog, type Address, type Hash, type TransactionReceipt } from 'viem';
import {
  LEGACY_FUNDING_ABI,
  lockStateAbiFor,
  lockStateFromRaw,
  readFactoryOwnership,
  readLockState,
  type RawLockState,
  UNCAPPED_INIT_ABI,
} from '@/contracts/readState';

export interface LockDetails {
  address: string;
  /**
   * The operator label for this grant, from the factory's lockName mapping.
   * Under several grants per wallet this is what identifies WHICH grant, so a
   * table keyed by beneficiary alone is now ambiguous. Empty string means either
   * a v7/v9 factory, which has no names at all, or a deliberately redacted one —
   * the UI cannot tell those apart and should not pretend to.
   */
  name: string;
  totalAmount: bigint;
  unlockedAmount: bigint;
  beneficiary: string;
  startTime: bigint;
  vestedAmount: bigint;
  /** What claim() pays right now — already capped by the lock's own balance. */
  availableAmount: bigint;
  isInitialized: boolean;
  isPaused: boolean;
  offboardTime: bigint;
  unlockScheduleAmount: bigint;
  /** Fixed date this lock's track releases its 25% cliff; 0 until the owner sets it. */
  unlockCliffTime: bigint;
  /** Unlock track; see Category in useWeb3. Fixed at deploy time, never editable. */
  category: number;
  /** Earned by the schedule and unclaimed, whether or not it has been funded. */
  earnedUnclaimed: bigint;
  /** Tokens actually held by the lock; the ceiling on what the beneficiary can take. */
  lockBalance: bigint;
  /** Next timestamp this beneficiary's entitlement grows; 0 when nothing more ever lands. */
  nextUnlockTime: bigint;
  /**
   * The ceiling on what this lock can ever pay — totalAmount for a healthy
   * grant, the frozen figure for an off-boarded one, and 0 for a cancelled one.
   * Every remaining/still-locked column belongs against this, not totalAmount.
   */
  maxEntitlement: bigint;
  /**
   * Terminal. The grant forfeited EVERYTHING its beneficiary had not already
   * claimed, including tokens they had earned and never collected, and its
   * whole balance was swept. It stays in the roster forever — nothing is ever
   * removed from allLockContracts — so the table has to say so on its own.
   */
  cancelled: boolean;
  /**
   * Tokens stranded in this lock above anything it can ever pay. Non-zero means
   * a top-up outlived the ceiling it was sized against; returnLockExcess is
   * what recovers it.
   */
  excessFunding: bigint;
  /** earnedUnclaimed that is not payable for lack of funding — someone is short now. */
  unfundedEarned: bigint;
  /** Tokens this lock still needs to cover everything through nextUnlockTime. */
  fundingDueNextUnlock: bigint;
}

/** Per-lock breakdown behind a funding total, from fundingPlanThrough. */
export interface FundingPlan {
  fundThrough: bigint;
  total: bigint;
  entries: { lock: Address; amount: bigint }[];
}

/**
 * The factory refuses a funding date further than MAX_FUNDING_LOOKAHEAD ahead, so
 * the panel can reject one before asking for a signature. Mirrors the constant in
 * TokenLockFactory; it is read from chain on load and this is only the fallback.
 */
const DEFAULT_MAX_FUNDING_LOOKAHEAD = BigInt(100 * 24 * 60 * 60);

const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000';

/**
 * Mirrors TokenLockFactory.MAX_LOCK_NAME_BYTES.
 *
 * BYTES of UTF-8, never String.length. The two differ by a factor of three for
 * CJK text — one such character is 3 bytes, so the real bound is about 21 of
 * them — and a form that counts characters lets through a label createLock
 * rejects. A roster is deployed entry by entry, so that discrepancy does not
 * surface as a validation message; it surfaces as a bulk deploy that stops half
 * way with some grants created and some not.
 */
export const MAX_LOCK_NAME_BYTES = 64;

/** What the contract measures. Use this in the form, not `name.length`. */
export const lockNameByteLength = (name: string): number =>
  new TextEncoder().encode(name).length;

/**
 * RPC batch size is independent of the UI page size. getLockState performs
 * several nested contract reads; 25 grants in a single eth_call can exceed a
 * public provider's limits. A 25-row page is fetched as 10 + 10 + 5 grants.
 */
const LOCK_DETAIL_BATCH_SIZE = 10;

export function useAdmin({ readOnly = false }: { readOnly?: boolean } = {}) {
  const { address, isConnected } = useAppKitAccount();
  const publicClient = usePublicClient({ chainId: environment.chainId });
  const { data: connectedWalletClient } = useWalletClient();
  // The public grants view cannot sign, even if the owner opens it.
  const walletClient = readOnly ? undefined : connectedWalletClient;

  const [incrementalFunding, setIncrementalFunding] = useState<boolean | null>(null);
  const [allLocks, setAllLocks] = useState<string[]>([]);
  // Lifetime tokens still to wire across every lock: the solvency number, not the
  // monthly one. calculateTotalTokensNeeded is gone — locks are no longer
  // pre-loaded with the whole allocation, so there is no single "needed" figure.
  // An off-boarded lock is priced at what it froze at, not its full allocation,
  // so a forfeited remainder is not carried as a phantom liability.
  const [remainingObligation, setRemainingObligation] = useState<bigint>(BigInt(0));
  // Earned but not payable for lack of funding. Anything above zero means a real
  // beneficiary is short right now and a top-up is overdue.
  const [unfundedEarned, setUnfundedEarned] = useState<bigint>(BigInt(0));
  // The date to fund through right now, always safe to pass to fundAllThrough.
  // Three states: block.timestamp when someone is already short, otherwise the
  // earliest tranche boundary clamped into MAX_FUNDING_LOOKAHEAD, and 0 only when
  // nothing is owed and nothing further will ever release.
  const [nextFundingDue, setNextFundingDue] = useState<bigint>(BigInt(0));
  // The mirror image of unfundedEarned: tokens sitting in locks above anything
  // those locks can ever pay. Above zero means a ceiling dropped after a top-up
  // — an off-board, or a cancellation that left something behind — and the
  // treasury is short by that much until returnLockExcess is called.
  const [totalExcessFunding, setTotalExcessFunding] = useState<bigint>(BigInt(0));
  const [factoryBalance, setFactoryBalance] = useState<bigint>(BigInt(0));
  const [maxFundingLookahead, setMaxFundingLookahead] = useState<bigint>(
    DEFAULT_MAX_FUNDING_LOOKAHEAD,
  );
  const [factoryOwner, setFactoryOwner] = useState<Address | null>(null);
  // Ownable2Step: set by transferOwnership, cleared when the new owner accepts.
  // A non-zero value means the handover is started but NOT finished.
  const [pendingOwner, setPendingOwner] = useState<Address | null>(null);
  const [tgeTime, setTgeTimeState] = useState<bigint>(BigInt(0));
  // Per-track unlock cliff dates read from the factory. 0 means the owner has not
  // pinned that track yet, and claims on it revert until they do.
  const [unlockCliffTimes, setUnlockCliffTimes] = useState<Record<Category, bigint>>({
    [Category.INVESTOR]: BigInt(0),
    [Category.TEAM]: BigInt(0),
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * Monotonic "the admin data was reloaded" counter, bumped exactly once per
   * loadAdminData call.
   *
   * It exists because the two things effects used to watch instead are both
   * wrong, and both wrong in the same expensive way:
   *
   *   - `isLoading` flips false -> true -> false, so an effect depending on it
   *     fires TWICE per load, and fires again for every unrelated action that
   *     touches the flag (every write in this hook does).
   *   - `allLocks` is a fresh array from `getAllLockContracts` on every load, so
   *     an effect depending on its identity re-runs even when the roster has not
   *     changed by a single address.
   *
   * Together those are why one admin action used to trigger three full reloads
   * of every lock in the roster. A counter fires exactly once, when there is
   * genuinely new data to read.
   *
   * Do NOT replace it with `allLocks.join(',')`. Several handlers refresh the
   * table by reloading and relying on that identity change; keyed on contents,
   * an action that changes a lock's state without changing the roster (funding,
   * pausing, off-boarding, cancelling) would leave the table stale.
   */
  const [dataVersion, setDataVersion] = useState(0);

  // A mined receipt can still represent a revert. Keep optimistic UI updates
  // and success notifications behind this check for every admin action.
  const waitForSuccessfulReceipt = async (hash: Hash) => {
    if (!publicClient) throw new Error('No RPC connection');
    const receipt = await publicClient.waitForTransactionReceipt({ hash });
    if (receipt.status === 'reverted') {
      throw new Error('The transaction reverted on-chain. No changes were made.');
    }
    return receipt;
  };

  const loadAdminData = async () => {
    if (!publicClient || (!readOnly && (!isConnected || !address))) return;

    try {
      setIsLoading(true);
      const factoryAddress = environment.factoryAddress as Address;

      setError(null);
      const ownership = await readFactoryOwnership(publicClient, factoryAddress);
      const locks = await publicClient.readContract({
        address: factoryAddress,
        abi: TOKEN_LOCK_FACTORY_ABI,
        functionName: 'getAllLockContracts',
      }) as Address[];
      setIncrementalFunding(ownership.incrementalFunding);
      setFactoryOwner(ownership.owner);
      setPendingOwner(ownership.pendingOwner === ZERO_ADDRESS ? null : ownership.pendingOwner);
      setAllLocks(locks);

      if (readOnly) {
        // Configuration reads need no wallet and no factory-wide funding quote.
        // Fail visibly if a schedule cannot be read; zero means genuinely unset.
        const [tge, investorCliff, teamCliff] = await Promise.all([
          publicClient.readContract({ address: factoryAddress, abi: TOKEN_LOCK_FACTORY_ABI, functionName: 'tgeTime' }),
          publicClient.readContract({ address: factoryAddress, abi: TOKEN_LOCK_FACTORY_ABI, functionName: 'unlockCliffTime', args: [Category.INVESTOR] }),
          publicClient.readContract({ address: factoryAddress, abi: TOKEN_LOCK_FACTORY_ABI, functionName: 'unlockCliffTime', args: [Category.TEAM] }),
        ]);
        setTgeTimeState(tge);
        setUnlockCliffTimes({ [Category.INVESTOR]: investorCliff, [Category.TEAM]: teamCliff });
        return;
      }

      if (!ownership.incrementalFunding) {
        const [needed, tokenAddress] = await Promise.all([
          publicClient.readContract({ address: factoryAddress, abi: LEGACY_FUNDING_ABI, functionName: 'calculateTotalTokensNeeded' }) as Promise<bigint>,
          publicClient.readContract({ address: factoryAddress, abi: TOKEN_LOCK_FACTORY_ABI, functionName: 'token' }) as Promise<Address>,
        ]);
        const balance = await publicClient.readContract({ address: tokenAddress, abi: ERC20_ABI, functionName: 'balanceOf', args: [factoryAddress] }) as bigint;
        setRemainingObligation(needed);
        setFactoryBalance(balance);
        setUnfundedEarned(0n);
        setNextFundingDue(0n);
        setTotalExcessFunding(0n);
        return;
      }

      // The funding desk reads: what is still owed for the whole schedule, who is
      // already short, when the next tranche lands, and what is on hand to pay it.
      //
      // Every one of these is an ON-CHAIN aggregate and must stay that way. The
      // obvious "optimisation" — reduce over the paged per-lock reads the table
      // already fetches — is wrong in a specific and expensive way: those pages
      // run with allowFailure, so a dropped page silently contributes 0 and the
      // total reads LOWER than the truth. A funding figure that is too low is
      // not a display bug. The panel gates the top-up button on it, so it
      // refuses to send a top-up that a real beneficiary is genuinely waiting
      // for, and it does so while looking perfectly healthy. The contract loops
      // its whole roster in one call and either answers or reverts.
      const [obligation, unfunded, dueAt, lookahead, tokenAddress] =
        await Promise.all([
          publicClient.readContract({
            address: factoryAddress,
            abi: TOKEN_LOCK_FACTORY_ABI,
            functionName: 'totalRemainingObligation',
          }) as Promise<bigint>,
          publicClient.readContract({
            address: factoryAddress,
            abi: TOKEN_LOCK_FACTORY_ABI,
            functionName: 'totalUnfundedEarned',
          }) as Promise<bigint>,
          publicClient.readContract({
            address: factoryAddress,
            abi: TOKEN_LOCK_FACTORY_ABI,
            functionName: 'nextFundingDue',
          }) as Promise<bigint>,
          publicClient.readContract({
            address: factoryAddress,
            abi: TOKEN_LOCK_FACTORY_ABI,
            functionName: 'MAX_FUNDING_LOOKAHEAD',
          }) as Promise<bigint>,
          publicClient.readContract({
            address: factoryAddress,
            abi: TOKEN_LOCK_FACTORY_ABI,
            functionName: 'token',
          }) as Promise<Address>,
        ]);

      const balance = (await publicClient.readContract({
        address: tokenAddress,
        abi: ERC20_ABI,
        functionName: 'balanceOf',
        args: [factoryAddress],
      })) as bigint;

      // totalExcessFunding only exists on v10; a v9 factory has no notion of a
      // ceiling dropping below what was already wired.
      const excess = environment.factoryAbiVersion === 'v10'
        ? (await publicClient.readContract({
            address: factoryAddress,
            abi: TOKEN_LOCK_FACTORY_ABI,
            functionName: 'totalExcessFunding',
          })) as bigint
        : 0n;

      setRemainingObligation(obligation);
      setUnfundedEarned(unfunded);
      setNextFundingDue(dueAt);
      setMaxFundingLookahead(lookahead);
      setFactoryBalance(balance);
      setTotalExcessFunding(excess);
    } catch (err: any) {
      console.error('Error loading admin data:', err);
      setError(err.message || 'Failed to load admin data');
    } finally {
      setIsLoading(false);
      // Once per load attempt, success or failure. Anything that needs to react
      // to "there is new admin data" depends on this, never on isLoading.
      setDataVersion((version) => version + 1);
    }
  };

  const getLockDetails = async (lockAddress: Address): Promise<LockDetails | null> => {
    if (!publicClient) return null;

    try {
      const version = environment.factoryAbiVersion;
      const info = await readLockState(publicClient, lockAddress, version);
      const { earnedUnclaimed, lockBalance, nextUnlockTime } = info;

      const paused = (await publicClient.readContract({
        address: lockAddress,
        abi: TOKEN_LOCK_ABI,
        functionName: 'paused',
      })) as boolean;

      const name = version === 'v10'
        ? (await publicClient.readContract({
            address: environment.factoryAddress as Address,
            abi: TOKEN_LOCK_FACTORY_ABI,
            functionName: 'lockName',
            args: [lockAddress],
          })) as string
        : '';

      // What this lock still needs so the next tranche is payable in full. This
      // is the health signal: zero means funded through the next unlock, above
      // zero means a top-up is due before that date. A cancelled lock reports
      // nextUnlockTime 0 and so lands here at zero, which is correct — nothing
      // further will ever be released to it.
      const fundingDueNextUnlock =
        nextUnlockTime > BigInt(0)
          ? ((await publicClient.readContract({
              address: lockAddress,
              abi: TOKEN_LOCK_ABI,
              functionName: 'fundingRequiredThrough',
              args: [nextUnlockTime],
            })) as bigint)
          : BigInt(0);

      return {
        address: lockAddress,
        name,
        ...info,
        isPaused: paused,
        unfundedEarned:
          earnedUnclaimed > lockBalance ? earnedUnclaimed - lockBalance : BigInt(0),
        fundingDueNextUnlock,
      };
    } catch (err) {
      console.error('Error fetching lock details:', err);
      return null;
    }
  };

  /**
   * Every lock's details in paged Multicall3 batches, keyed by lock address.
   *
   * This replaces a serial `for (const lock of allLocks) await getLockDetails()`
   * loop, which cost three sequential round trips per lock: fifty grants was a
   * hundred and fifty RPC calls in strict sequence, run again on every reload.
   *
   * Three properties are load-bearing:
   *
   *   - `allowFailure: true`, so one unreadable lock returns a hole instead of
   *     rejecting the batch and blanking the whole table. The hole is simply
   *     omitted from the result: a missing row is honest, a zeroed row is not.
   *   - ONE explicit `blockNumber` for the entire refresh, taken before the
   *     first page. Without it each page is quoted at whatever block its node
   *     happened to be on, so a claim or a top-up landing mid-refresh shows up
   *     in some rows and not others, and the totals in the table would not add
   *     up against each other. This is also why the aggregates above are read
   *     from the contract rather than summed from these rows.
   *   - Two passes, because fundingRequiredThrough takes each lock's own
   *     nextUnlockTime, which is only known after the first pass decodes.
   */
  const getAllLockDetails = async (
    lockAddresses: readonly Address[],
    signal?: AbortSignal,
  ): Promise<Record<string, LockDetails>> => {
    const details: Record<string, LockDetails> = {};
    if (!publicClient || lockAddresses.length === 0) return details;

    const version = environment.factoryAbiVersion;
    const stateAbi = lockStateAbiFor(version);
    const factoryAddress = environment.factoryAddress as Address;
    const readRpc = createRpcReader(signal);
    const blockNumber = await readRpc(() => publicClient.getBlockNumber());
    // Our own small batches are sequential. viem's default byte limit would
    // split them again and send those chunks concurrently (notably names).
    const readBatch = async (contracts: unknown[]) => {
      type Result = { status: 'success' | 'failure'; result?: unknown; error?: unknown };
      const results: Result[] = contracts.map(() => ({ status: 'failure' }));
      let remaining = contracts.map((_, index) => index);
      try {
        await readRpc(async () => {
          const batch = await publicClient.multicall({
            contracts: remaining.map((index) => contracts[index]) as any,
            allowFailure: true,
            batchSize: 0,
            blockNumber,
          }) as Result[];
          remaining.forEach((index, i) => {
            results[index] = batch[i] ?? { status: 'failure' };
          });
          // With allowFailure, viem may return transport failures as entries
          // instead of throwing. Keep successes and retry only transient holes.
          remaining = remaining.filter((index) =>
            results[index].status === 'failure' && isRetryableRpcError(results[index].error));
          if (remaining.length > 0) throw results[remaining[0]].error;
        });
      } catch {
        signal?.throwIfAborted();
        // Preserve other successful batches; the UI offers retry for holes.
      }
      return results;
    };

    type Pending = {
      address: Address;
      state: RawLockState;
      isPaused: boolean;
      name: string;
    };
    const pending: Pending[] = [];

    for (let offset = 0; offset < lockAddresses.length; offset += LOCK_DETAIL_BATCH_SIZE) {
      const page = lockAddresses.slice(offset, offset + LOCK_DETAIL_BATCH_SIZE);
      const contracts: unknown[] = page.flatMap((lock) => [
        { address: lock, abi: stateAbi, functionName: 'getLockState' },
        { address: lock, abi: TOKEN_LOCK_ABI, functionName: 'paused' },
      ]);
      // The whole page's labels in one more call on the same block, rather than
      // one round trip per grant on the panel's most expensive path.
      if (version === 'v10') {
        contracts.push({
          address: factoryAddress,
          abi: TOKEN_LOCK_FACTORY_ABI,
          functionName: 'getLockNames',
          args: [page],
        });
      }

      const results = await readBatch(contracts);

      const namesResult = version === 'v10' ? results[page.length * 2] : undefined;
      const names = namesResult?.status === 'success'
        ? (namesResult.result as readonly string[])
        : [];

      page.forEach((lock, i) => {
        const stateResult = results[i * 2];
        const pausedResult = results[i * 2 + 1];
        // Without the state there is no row to show. Drop it rather than
        // inventing zeros for a lock that may hold real tokens.
        if (stateResult?.status !== 'success' || pausedResult?.status !== 'success') return;
        if (version === 'v10' && typeof names[i] !== 'string') return;
        pending.push({
          address: lock,
          state: stateResult.result as RawLockState,
          isPaused: pausedResult.result as boolean,
          name: names[i] ?? '',
        });
      });
    }

    // lockStateFromRaw is awaited rather than inlined so the positional
    // defaults, the v7 token()/balanceOf() fallback and the availableAmount
    // clamp stay in the one place that owns them. On v7 that fallback costs two
    // extra reads per lock; v7 is a legacy escape hatch and correctness there
    // beats a round trip.
    // Legacy normalization needs token/balance reads. Keep those sequential
    // too, pinned to the same block, rather than fanning out per grant.
    const infos: Awaited<ReturnType<typeof lockStateFromRaw>>[] = [];
    for (let i = 0; i < pending.length; i++) {
      signal?.throwIfAborted();
      const entry = pending[i]!;
      try {
        infos.push(await lockStateFromRaw({
          readContract: (args) => readRpc(() => publicClient.readContract({ ...args, blockNumber })),
        }, entry.address, version, entry.state));
      } catch {
        signal?.throwIfAborted();
        pending.splice(i--, 1);
      }
    }

    // Pass two: what each lock still needs to cover its own next tranche.
    const dueByLock = new Map<string, bigint>();
    const needsQuote = pending
      .map((entry, i) => ({ address: entry.address, nextUnlockTime: infos[i]!.nextUnlockTime }))
      .filter((entry) => entry.nextUnlockTime > BigInt(0));

    for (let offset = 0; offset < needsQuote.length; offset += LOCK_DETAIL_BATCH_SIZE) {
      const page = needsQuote.slice(offset, offset + LOCK_DETAIL_BATCH_SIZE);
      const results = await readBatch(page.map((entry) => ({
          address: entry.address,
          abi: TOKEN_LOCK_ABI,
          functionName: 'fundingRequiredThrough',
          args: [entry.nextUnlockTime],
        })));

      page.forEach((entry, i) => {
        const result = results[i];
        if (result?.status === 'success') {
          dueByLock.set(entry.address.toLowerCase(), result.result as bigint);
        }
      });
    }

    pending.forEach((entry, i) => {
      const info = infos[i]!;
      // A missing funding quote is unknown, never a healthy zero shortfall.
      if (info.nextUnlockTime > 0n && !dueByLock.has(entry.address.toLowerCase())) return;
      details[entry.address] = {
        address: entry.address,
        name: entry.name,
        ...info,
        isPaused: entry.isPaused,
        unfundedEarned: info.earnedUnclaimed > info.lockBalance
          ? info.earnedUnclaimed - info.lockBalance
          : BigInt(0),
        fundingDueNextUnlock: dueByLock.get(entry.address.toLowerCase()) ?? BigInt(0),
      };
    });

    return details;
  };

  const pauseFactory = async () => {
    if (!walletClient || !publicClient) {
      throw new Error('Wallet not connected');
    }

    try {
      setIsLoading(true);
      const factoryAddress = environment.factoryAddress as Address;

      const hash = await walletClient.writeContract({
        address: factoryAddress,
        abi: TOKEN_LOCK_FACTORY_ABI,
        functionName: 'pause',
        args: [],
      });

      await waitForSuccessfulReceipt(hash);
    } catch (err: any) {
      throw new Error(err.message || 'Failed to pause factory');
    } finally {
      setIsLoading(false);
    }
  };

  const unpauseFactory = async () => {
    if (!walletClient || !publicClient) {
      throw new Error('Wallet not connected');
    }

    try {
      setIsLoading(true);
      const factoryAddress = environment.factoryAddress as Address;

      const hash = await walletClient.writeContract({
        address: factoryAddress,
        abi: TOKEN_LOCK_FACTORY_ABI,
        functionName: 'unpause',
        args: [],
      });

      await waitForSuccessfulReceipt(hash);
    } catch (err: any) {
      throw new Error(err.message || 'Failed to unpause factory');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Pull one event out of a receipt this hook's own transaction produced.
   *
   * Logs from other contracts in the same transaction decode as garbage or throw
   * outright, so each candidate is filtered to the factory's own address first
   * and decoded inside a try. `receipt.logs` is read defensively: a receipt with
   * no logs array at all is possible from some providers, and the caller wants
   * "I could not tell you", not a TypeError.
   */
  const findFactoryEvent = <T,>(receipt: TransactionReceipt, eventName: string): T | null => {
    const factoryAddress = (environment.factoryAddress as string).toLowerCase();
    for (const log of receipt.logs ?? []) {
      if (log.address?.toLowerCase() !== factoryAddress) continue;
      try {
        const decoded = decodeEventLog({
          abi: TOKEN_LOCK_FACTORY_ABI,
          data: log.data,
          topics: log.topics,
        });
        if (decoded.eventName === eventName) return decoded.args as unknown as T;
      } catch {
        // Not an event this ABI knows. Nothing to do but keep looking.
      }
    }
    return null;
  };

  /**
   * Create a grant.
   *
   * `name` is REQUIRED, because it is what identifies WHICH grant now that one
   * address may hold several — and it is validated in BYTES here, before a
   * signature is requested, so a 22-character CJK label fails in the form rather
   * than halfway through a bulk roster deploy.
   *
   * Returns the new lock's address, decoded from the LockContractCreated event
   * in this transaction's own receipt, or null if it could not be read.
   *
   * It can no longer be looked up afterwards. The old code read
   * getLockContract(beneficiary) after the write; that selector is gone, and had
   * it survived it would now be actively dangerous — an address holding two
   * grants would return whichever one that mapping happened to hold, so the
   * panel would confidently open, fund or cancel the WRONG grant. The receipt
   * names the lock this transaction created and nothing else can.
   */
  const createLock = async (
    beneficiary: string,
    amount: string,
    startTime: number,
    category: Category,
    name: string,
  ): Promise<Address | null> => {
    if (!walletClient || !publicClient) {
      throw new Error('Wallet not connected');
    }

    const nameBytes = lockNameByteLength(name);
    if (nameBytes === 0) {
      throw new Error('A grant name is required — it is what identifies this grant.');
    }
    if (nameBytes > MAX_LOCK_NAME_BYTES) {
      throw new Error(
        `Grant name is ${nameBytes} bytes of UTF-8; the limit is ${MAX_LOCK_NAME_BYTES}. ` +
        'Note the contract counts bytes, not characters.',
      );
    }

    try {
      setIsLoading(true);
      const factoryAddress = environment.factoryAddress as Address;
      const decimals = environment.tokenDecimals;
      const amountInWei = BigInt(amount) * BigInt(10 ** decimals);

      const hash = await walletClient.writeContract({
        address: factoryAddress,
        abi: TOKEN_LOCK_FACTORY_ABI,
        functionName: 'createLock',
        // category is a uint8 enum, so it stays a plain number — a bigint here would
        // not encode; every other numeric arg on this call is a bigint.
        args: [beneficiary as Address, amountInWei, BigInt(startTime), category, name],
      });

      const receipt = await waitForSuccessfulReceipt(hash);
      const created = findFactoryEvent<{ lockContract: Address }>(receipt, 'LockContractCreated');
      return created?.lockContract ?? null;
    } catch (err: any) {
      throw new Error(err.message || 'Failed to create lock');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Arm one lock and catch it up to today, capped at `maxTotal`.
   *
   * `maxTotal` is not optional and is not an estimate: it must be a
   * `fundingRequiredThrough(lock, now)` quote taken immediately before, which is
   * what the caller showed the operator. The factory reverts
   * FundingExceedsMax(required, maxTotal) rather than moving more than that.
   *
   * This call used to take no cap at all. That was survivable only while an
   * allocation was immutable; setTotalAmount is now legal at any point in a
   * grant's life, so a raise landing between the quote and the signature made an
   * uncapped call move whatever the new allocation said — bounded by nothing but
   * the factory's balance. Every other funding entrypoint has always been
   * quote-then-sign; these two are now the same shape.
   */
  const initializeLock = async (lockAddress: Address, maxTotal: bigint) => {
    if (!walletClient || !publicClient) {
      throw new Error('Wallet not connected');
    }

    try {
      setIsLoading(true);
      const factoryAddress = environment.factoryAddress as Address;

      // The cap only exists on v10. Encoding it against a v7 or v9 factory
      // would be a different selector and an unknown function to them, which is
      // how this call silently stopped working on every live deployment the
      // first time the capped shape was wired in unconditionally.
      const hash = environment.factoryAbiVersion === 'v10'
        ? await walletClient.writeContract({
            address: factoryAddress,
            abi: TOKEN_LOCK_FACTORY_ABI,
            functionName: 'initializeLock',
            args: [lockAddress, maxTotal],
          })
        : await walletClient.writeContract({
            address: factoryAddress,
            abi: UNCAPPED_INIT_ABI,
            functionName: 'initializeLock',
            args: [lockAddress],
          });

      await waitForSuccessfulReceipt(hash);
    } catch (err: any) {
      throw new Error(err.message || 'Failed to initialize lock');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * The same call across every lock, capped at `maxTotal` for the same reason.
   * The quote to pass is `totalFundingRequiredThrough(now)`, read immediately
   * before — getTotalFundingRequiredThrough below.
   */
  const initializeAllLocks = async (maxTotal: bigint) => {
    if (!walletClient || !publicClient) {
      throw new Error('Wallet not connected');
    }

    try {
      setIsLoading(true);
      const factoryAddress = environment.factoryAddress as Address;

      // See initializeLock: pre-v10 factories have no cap to pass, and the
      // quote the caller computed is unenforceable against them. The UI has to
      // say so rather than implying a ceiling that the chain will not honour.
      const hash = environment.factoryAbiVersion === 'v10'
        ? await walletClient.writeContract({
            address: factoryAddress,
            abi: TOKEN_LOCK_FACTORY_ABI,
            functionName: 'initializeAll',
            args: [maxTotal],
          })
        : await walletClient.writeContract({
            address: factoryAddress,
            abi: UNCAPPED_INIT_ABI,
            functionName: 'initializeAll',
            args: [],
          });

      await waitForSuccessfulReceipt(hash);
    } catch (err: any) {
      throw new Error(err.message || 'Failed to initialize all locks');
    } finally {
      setIsLoading(false);
    }
  };

  // ------------------------------------------------------------------
  // Funding — the recurring job. Pick a date, the contract prices it, then push
  // exactly that. No call ever moves more than the previewed total.
  // ------------------------------------------------------------------

  /**
   * "Now", as the CHAIN measures it: the timestamp of the latest block.
   *
   * Every funding call prices itself against `block.timestamp`, so a quote taken
   * against the browser clock is a quote for a different moment. The two errors
   * are not symmetric and only one of them is safe:
   *
   *   - A browser clock BEHIND the chain quotes a smaller figure than the
   *     requirement, so the cap binds, the call reverts FundingExceedsMax and
   *     moves nothing. Annoying, and the operator re-quotes.
   *   - A browser clock AHEAD of the chain prices in tranches the chain has not
   *     released yet. The operator then reviews and signs a cap LARGER than the
   *     true shortfall, and a cap above the requirement cannot bind — which is
   *     the whole protection gone, silently, on a machine whose only fault is a
   *     fast clock.
   *
   * The latest block's timestamp is by construction at or before the timestamp
   * of the block that mines the transaction, so a quote taken from it can only
   * err in the direction that makes the cap bind.
   *
   * It throws rather than falling back to `Date.now()`: a caller that cannot
   * reach the chain must say so, not quietly re-introduce the unsafe clock.
   */
  const getChainTimestamp = async (): Promise<bigint> => {
    if (!publicClient) throw new Error('No RPC connection');
    const block = await publicClient.getBlock();
    return block.timestamp;
  };

  /** Total to wire so every lock is funded through `fundThrough`. */
  const getTotalFundingRequiredThrough = async (fundThrough: bigint): Promise<bigint> => {
    if (!publicClient) throw new Error('No RPC connection');
    return (await publicClient.readContract({
      address: environment.factoryAddress as Address,
      abi: TOKEN_LOCK_FACTORY_ABI,
      functionName: 'totalFundingRequiredThrough',
      args: [fundThrough],
    })) as bigint;
  };

  /**
   * What ONE lock still needs so everything it has released through
   * `fundThrough` is payable. The per-lock sibling of the total above, and the
   * cap to sign initializeLock with: pass `fundThrough = now` there, since that
   * is the date initializeLock funds through.
   */
  const getLockFundingRequiredThrough = async (
    lockAddress: Address,
    fundThrough: bigint,
  ): Promise<bigint> => {
    if (!publicClient) throw new Error('No RPC connection');
    return (await publicClient.readContract({
      address: environment.factoryAddress as Address,
      abi: TOKEN_LOCK_FACTORY_ABI,
      functionName: 'fundingRequiredThrough',
      args: [lockAddress, fundThrough],
    })) as bigint;
  };

  /** The same figure with its per-lock breakdown, for review before signing. */
  const getFundingPlan = async (fundThrough: bigint): Promise<FundingPlan> => {
    if (!publicClient) throw new Error('No RPC connection');
    const factoryAddress = environment.factoryAddress as Address;

    const count = (await publicClient.readContract({
      address: factoryAddress,
      abi: TOKEN_LOCK_FACTORY_ABI,
      functionName: 'lockCount',
    })) as bigint;

    const [locks, amounts, total] = (await publicClient.readContract({
      address: factoryAddress,
      abi: TOKEN_LOCK_FACTORY_ABI,
      functionName: 'fundingPlanThrough',
      args: [fundThrough, BigInt(0), count],
    })) as [Address[], bigint[], bigint];

    return {
      fundThrough,
      total,
      entries: locks.map((lock, i) => ({ lock, amount: amounts[i] ?? BigInt(0) })),
    };
  };

  /**
   * The monthly call. `maxTotal` must be the previewed total: the factory reverts
   * FundingExceedsMax if the real requirement has grown past it since the quote,
   * which is the protection against signing a stale, larger number.
   */
  const fundAllThrough = async (fundThrough: bigint, maxTotal: bigint) => {
    if (!incrementalFunding) throw new Error('This factory does not support periodic funding.');
    if (!walletClient || !publicClient) {
      throw new Error('Wallet not connected');
    }
    try {
      setIsLoading(true);
      const factoryAddress = environment.factoryAddress as Address;
      const hash = await walletClient.writeContract({
        address: factoryAddress,
        abi: TOKEN_LOCK_FACTORY_ABI,
        functionName: 'fundAllThrough',
        args: [fundThrough, maxTotal],
      });
      await waitForSuccessfulReceipt(hash);
    } finally {
      setIsLoading(false);
    }
  };

  // ------------------------------------------------------------------
  // Ownership — two-step. transferOwnership only nominates; nothing changes
  // hands until the nominee calls acceptOwnership.
  // ------------------------------------------------------------------

  const transferOwnership = async (newOwner: Address) => {
    if (!incrementalFunding) throw new Error('This factory does not support two-step ownership.');
    if (!walletClient || !publicClient) {
      throw new Error('Wallet not connected');
    }
    try {
      setIsLoading(true);
      const hash = await walletClient.writeContract({
        address: environment.factoryAddress as Address,
        abi: TOKEN_LOCK_FACTORY_ABI,
        functionName: 'transferOwnership',
        args: [newOwner],
      });
      await waitForSuccessfulReceipt(hash);
      setPendingOwner(newOwner === ZERO_ADDRESS ? null : newOwner);
    } finally {
      setIsLoading(false);
    }
  };

  /** Clears a pending nomination; ownership stays exactly where it is. */
  const cancelOwnershipTransfer = async () => transferOwnership(ZERO_ADDRESS as Address);

  /** Callable only by the pending owner — this is the step that completes it. */
  const acceptOwnership = async () => {
    if (!incrementalFunding) throw new Error('This factory does not support two-step ownership.');
    if (!walletClient || !publicClient) {
      throw new Error('Wallet not connected');
    }
    try {
      setIsLoading(true);
      const hash = await walletClient.writeContract({
        address: environment.factoryAddress as Address,
        abi: TOKEN_LOCK_FACTORY_ABI,
        functionName: 'acceptOwnership',
        args: [],
      });
      await waitForSuccessfulReceipt(hash);
      setPendingOwner(null);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Move ONE grant to a new wallet. This replaces changeBeneficiary.
   *
   * The rename is not cosmetic and must not be undone. The old call took
   * (oldBeneficiary, newBeneficiary) and this one takes (lock, newBeneficiary):
   * two address arguments either way, therefore the SAME four-byte selector with
   * a different meaning. Under the old name a stale bundle, a cached runbook or
   * a saved Safe transaction could encode a beneficiary where a lock address is
   * now expected and the call would be accepted for decoding, rejected only by
   * the registry check. Under a different name such a call cannot be built.
   *
   * It takes the lock rather than the beneficiary for the same reason createLock
   * returns one: a beneficiary is no longer a unique reference. An investor with
   * two grants moving one to a new wallet has to be able to say which.
   */
  const reassignLock = async (lockAddress: Address, newBeneficiary: Address) => {
    if (!walletClient || !publicClient) {
      throw new Error('Wallet not connected');
    }

    try {
      setIsLoading(true);
      const factoryAddress = environment.factoryAddress as Address;

      const hash = await walletClient.writeContract({
        address: factoryAddress,
        abi: TOKEN_LOCK_FACTORY_ABI,
        functionName: 'reassignLock',
        args: [lockAddress, newBeneficiary],
      });

      await waitForSuccessfulReceipt(hash);
    } catch (err: any) {
      throw new Error(err.message || 'Failed to reassign lock');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Relabel a grant. The empty string is accepted on chain here and only here,
   * for redaction — though redaction is not removal: the original stays in the
   * calldata, in the LockNameSet event and on every archive node forever.
   */
  const setLockName = async (lockAddress: Address, name: string) => {
    if (!walletClient || !publicClient) {
      throw new Error('Wallet not connected');
    }

    const nameBytes = lockNameByteLength(name);
    if (nameBytes > MAX_LOCK_NAME_BYTES) {
      throw new Error(
        `Grant name is ${nameBytes} bytes of UTF-8; the limit is ${MAX_LOCK_NAME_BYTES}. ` +
        'Note the contract counts bytes, not characters.',
      );
    }

    try {
      setIsLoading(true);
      const hash = await walletClient.writeContract({
        address: environment.factoryAddress as Address,
        abi: TOKEN_LOCK_FACTORY_ABI,
        functionName: 'setLockName',
        args: [lockAddress, name],
      });
      await waitForSuccessfulReceipt(hash);
    } catch (err: any) {
      throw new Error(err.message || 'Failed to set lock name');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Resize a grant. Legal at ANY point in its life, in EITHER direction.
   *
   * There is no pre-start window and no client-side gate: the only refusals are
   * a cancelled grant (LockIsCancelled), zero (InvalidAmount), above
   * MAX_TOTAL_AMOUNT (AmountAboveMax) and the value it already has (SameAmount).
   *
   * It works because the ledger and the schedule are separate. `unlockedAmount`
   * — what has actually been paid — is never touched, and both curves are
   * homogeneous of degree one in totalAmount, so a resize rescales the whole
   * entitlement curve by newTotal/oldTotal, the PAST INCLUDED, and
   * earnedUnclaimed re-reads as `entitled(t) - unlockedAmount` from the next
   * block. Nothing is migrated or reconciled.
   *
   * Both directions therefore reach backwards, and the caller must say so before
   * anything is signed:
   *
   *   RAISING a grant that is already part way through makes the difference owed
   *   IMMEDIATELY, claimable as soon as the lock is funded for it.
   *
   *   LOWERING rescales the past too, so a holder who has already taken more
   *   than the smaller curve says they should have by now receives nothing
   *   further until it catches up — which can be years — and nothing further at
   *   all if the new total is below what they have already claimed. That state,
   *   `unlockedAmount > totalAmount`, is deliberately representable: refusing it
   *   would leave the ledger permanently overstating the grant. Tokens already
   *   paid are not recalled by arithmetic; emergencyWithdrawFrom is the separate,
   *   loud transaction for that, and tokens merely stranded in the lock become
   *   excessFunding() and come back through returnLockExcess.
   *
   * The amount is in token base units; the caller does the decimal conversion.
   */
  const setLockTotalAmount = async (lockAddress: Address, newTotalAmount: bigint) => {
    if (!walletClient || !publicClient) {
      throw new Error('Wallet not connected');
    }
    try {
      setIsLoading(true);
      const hash = await walletClient.writeContract({
        address: environment.factoryAddress as Address,
        abi: TOKEN_LOCK_FACTORY_ABI,
        functionName: 'setLockTotalAmount',
        args: [lockAddress, newTotalAmount],
      });
      await waitForSuccessfulReceipt(hash);
    } catch (err: any) {
      throw new Error(err.message || 'Failed to change the grant amount');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Cancel a grant and sweep its balance to `recipient`, in one transaction.
   *
   * THIS IS FULL CLAWBACK AND IT IS IRREVERSIBLE. The beneficiary forfeits
   * everything they have not already claimed, INCLUDING tokens they had vested
   * and earned but never collected. maxEntitlement drops to 0, so vested,
   * entitled, earned-unclaimed, claimable and nextUnlockTime all follow it to 0,
   * the lock leaves totalRemainingObligation, the funding loops stop pushing to
   * it, and its entire balance is swept in the same call. Only tokens they have
   * already taken are out of reach. A second cancel reverts AlreadyCancelled,
   * and setOffboardTime, setTotalAmount and reassignLock all refuse afterwards.
   *
   * Confirm destructively before calling this, and note what it silences: a
   * merely swept lock keeps reporting its debt through totalUnfundedEarned, and
   * a cancelled one does not. The LockCancelled, Cancelled and EmergencyWithdraw
   * events are the only remaining signal that it happened.
   *
   * The lock stays in the roster forever — nothing is ever removed from
   * allLockContracts, so lockCount() does not move and no later index shifts.
   *
   * Returns the figures from the LockCancelled event: `forfeited` is what the
   * beneficiary lost, `swept` is what actually came back, and they are different
   * numbers because a lock only ever holds what has been wired to it.
   */
  const cancelLock = async (
    lockAddress: Address,
    recipient: Address,
  ): Promise<{ forfeited: bigint; swept: bigint } | null> => {
    if (!walletClient || !publicClient) {
      throw new Error('Wallet not connected');
    }
    try {
      setIsLoading(true);
      const hash = await walletClient.writeContract({
        address: environment.factoryAddress as Address,
        abi: TOKEN_LOCK_FACTORY_ABI,
        functionName: 'cancelLock',
        args: [lockAddress, recipient],
      });
      const receipt = await waitForSuccessfulReceipt(hash);
      const event = findFactoryEvent<{ forfeited: bigint; swept: bigint }>(
        receipt, 'LockCancelled');
      return event ? { forfeited: event.forfeited, swept: event.swept } : null;
    } catch (err: any) {
      throw new Error(err.message || 'Failed to cancel the grant');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Recover tokens stranded in a lock above anything it can ever pay.
   *
   * Funding only ever pushes, so a ceiling that drops after a top-up — an
   * off-board, or a cancellation that somehow left something behind — leaves a
   * residue nothing else can reach. The contract REVERTS InvalidAmount on zero
   * rather than no-opping: a transaction that costs gas and looks successful
   * while moving nothing is worse than a refusal an operator can read. Returns
   * the amount recovered.
   */
  const returnLockExcess = async (
    lockAddress: Address,
    recipient: Address,
  ): Promise<bigint | null> => {
    if (!walletClient || !publicClient) {
      throw new Error('Wallet not connected');
    }
    try {
      setIsLoading(true);
      const hash = await walletClient.writeContract({
        address: environment.factoryAddress as Address,
        abi: TOKEN_LOCK_FACTORY_ABI,
        functionName: 'returnLockExcess',
        args: [lockAddress, recipient],
      });
      const receipt = await waitForSuccessfulReceipt(hash);
      const event = findFactoryEvent<{ amount: bigint }>(receipt, 'LockExcessReturned');
      return event?.amount ?? null;
    } catch (err: any) {
      throw new Error(err.message || 'Failed to return excess funding');
    } finally {
      setIsLoading(false);
    }
  };

  const pauseLock = async (lockAddress: Address) => {
    if (!walletClient || !publicClient) {
      throw new Error('Wallet not connected');
    }

    try {
      setIsLoading(true);
      const factoryAddress = environment.factoryAddress as Address;

      const hash = await walletClient.writeContract({
        address: factoryAddress,
        abi: TOKEN_LOCK_FACTORY_ABI,
        functionName: 'pauseLockContract',
        args: [lockAddress],
      });

      await waitForSuccessfulReceipt(hash);
    } catch (err: any) {
      throw new Error(err.message || 'Failed to pause lock');
    } finally {
      setIsLoading(false);
    }
  };

  const unpauseLock = async (lockAddress: Address) => {
    if (!walletClient || !publicClient) {
      throw new Error('Wallet not connected');
    }

    try {
      setIsLoading(true);
      const factoryAddress = environment.factoryAddress as Address;

      const hash = await walletClient.writeContract({
        address: factoryAddress,
        abi: TOKEN_LOCK_FACTORY_ABI,
        functionName: 'unpauseLockContract',
        args: [lockAddress],
      });

      await waitForSuccessfulReceipt(hash);
    } catch (err: any) {
      throw new Error(err.message || 'Failed to unpause lock');
    } finally {
      setIsLoading(false);
    }
  };

  const setLockOffboardTime = async (lockAddress: Address, offboardTime: bigint) => {
    if (!walletClient || !publicClient) {
      throw new Error('Wallet not connected');
    }
    try {
      setIsLoading(true);
      const factoryAddress = environment.factoryAddress as Address;
      const hash = await walletClient.writeContract({
        address: factoryAddress,
        abi: TOKEN_LOCK_FACTORY_ABI,
        functionName: 'setLockOffboardTime',
        args: [lockAddress, offboardTime],
      });
      await waitForSuccessfulReceipt(hash);
    } catch (err: any) {
      throw new Error(err.message || 'Failed to set off-board time');
    } finally {
      setIsLoading(false);
    }
  };

  // Pin one track's unlock cliff. One-way per track on chain, so the caller must
  // have confirmed first; a date in the past unlocks that 25% immediately.
  const setUnlockCliff = async (category: Category, cliffUnixSeconds: bigint) => {
    if (!walletClient || !publicClient) {
      throw new Error('Wallet not connected');
    }
    try {
      setIsLoading(true);
      const factoryAddress = environment.factoryAddress as Address;
      const hash = await walletClient.writeContract({
        address: factoryAddress,
        abi: TOKEN_LOCK_FACTORY_ABI,
        functionName: 'setUnlockCliff',
        // category is a uint8 enum, so it stays a plain number; the date is a uint256.
        args: [category, cliffUnixSeconds],
      });
      await waitForSuccessfulReceipt(hash);
      setUnlockCliffTimes((prev) => ({ ...prev, [category]: cliffUnixSeconds }));
    } catch (err: any) {
      throw new Error(err.message || 'Failed to set unlock cliff');
    } finally {
      setIsLoading(false);
    }
  };

  const setTge = async (newTgeUnixSeconds: bigint) => {
    if (!walletClient || !publicClient) {
      throw new Error('Wallet not connected');
    }
    try {
      setIsLoading(true);
      const factoryAddress = environment.factoryAddress as Address;
      const hash = await walletClient.writeContract({
        address: factoryAddress,
        abi: TOKEN_LOCK_FACTORY_ABI,
        functionName: 'setTgeTime',
        args: [newTgeUnixSeconds],
      });
      await waitForSuccessfulReceipt(hash);
      setTgeTimeState(newTgeUnixSeconds);
    } catch (err: any) {
      throw new Error(err.message || 'Failed to set TGE');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (readOnly || (isConnected && address)) {
      loadAdminData();
    }
  }, [readOnly, isConnected, address, publicClient]);

  useEffect(() => {
    if (!publicClient || readOnly) return;
    (async () => {
      const factoryAddress = environment.factoryAddress as Address;
      try {
        const tge = (await publicClient.readContract({
          address: factoryAddress,
          abi: TOKEN_LOCK_FACTORY_ABI,
          functionName: 'tgeTime',
        })) as bigint;
        setTgeTimeState(tge);

        // Both tracks are read every time: each is set independently, and the dates
        // are owner-set per deployment, so nothing here may assume a value.
        const investorCliff = (await publicClient.readContract({
          address: factoryAddress,
          abi: TOKEN_LOCK_FACTORY_ABI,
          functionName: 'unlockCliffTime',
          args: [Category.INVESTOR],
        })) as bigint;
        const teamCliff = (await publicClient.readContract({
          address: factoryAddress,
          abi: TOKEN_LOCK_FACTORY_ABI,
          functionName: 'unlockCliffTime',
          args: [Category.TEAM],
        })) as bigint;
        setUnlockCliffTimes({
          [Category.INVESTOR]: investorCliff,
          [Category.TEAM]: teamCliff,
        });
      } catch (err) {
        console.error('Error loading factory schedule dates:', err);
      }
    })();
    // dataVersion, NOT isLoading. isLoading flips twice per load and once more
    // for every write in this hook, so this block used to re-read the factory's
    // schedule dates several times for each single admin action.
  }, [publicClient, dataVersion, readOnly]);

  return {
    // State
    allLocks,
    incrementalFunding,
    remainingObligation,
    unfundedEarned,
    nextFundingDue,
    totalExcessFunding,
    factoryBalance,
    maxFundingLookahead,
    factoryOwner,
    pendingOwner,
    tgeTime,
    unlockCliffTimes,
    isLoading,
    error,
    // Bumped once per loadAdminData. Effects that need to react to fresh admin
    // data depend on THIS, never on isLoading or on the allLocks array identity.
    dataVersion,

    // Actions
    loadAdminData,
    getLockDetails,
    getAllLockDetails,
    getChainTimestamp,
    getTotalFundingRequiredThrough,
    getLockFundingRequiredThrough,
    getFundingPlan,
    fundAllThrough,
    transferOwnership,
    cancelOwnershipTransfer,
    acceptOwnership,
    pauseFactory,
    unpauseFactory,
    createLock,
    initializeLock,
    initializeAllLocks,
    reassignLock,
    setLockName,
    setLockTotalAmount,
    cancelLock,
    returnLockExcess,
    pauseLock,
    unpauseLock,
    setLockOffboardTime,
    setTge,
    setUnlockCliff,
    setError,
  };
}
